import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Eye } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import NDNCAPartiesSection from "@/components/ndnca/NDNCAPartiesSection";
import NDNCAOtpBox from "@/components/ndnca/NDNCAOtpBox";
import NDNCASignedCard from "@/components/ndnca/NDNCASignedCard";
import useCountdown from "@/lib/useCountdown";

/** NDNCA signing dialog: pre-filled Aaron & Partners + user fills + OTP by email + PDF. */
export default function NDNCADialog({ open, setOpen, onSigned }) {
  const { lang, user, refreshUser } = useApp();
  const [tpl, setTpl] = useState(null);
  const [form, setForm] = useState({
    signer_name: user?.name || "",
    signer_fiscal_code: "",
    signer_address: "",
  });
  const [signature, setSignature] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);
  const [signatureMeta, setSignatureMeta] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const secondsLeft = useCountdown(otpExpiresAt);

  useEffect(() => {
    if (!open) return;
    api.get("/ndnca/template").then(({ data }) => setTpl(data)).catch(() => {});
  }, [open]);

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/request", { purpose: "ndnca" });
      setOtpEmail(data.email || "");
      setOtpRequested(true);
      setOtpExpiresAt(data.expires_at);
      toast.success(lang === "it" ? `Codice inviato a ${data.email}` : `Code sent to ${data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio OTP");
    } finally {
      setBusy(false);
    }
  };

  const sign = async () => {
    setBusy(true);
    try {
      // NDNCA is generated in Italian by default; use English template for
      // members whose browser language is not IT.
      const ndncaLang = (typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("it")) ? "it" : "en";
      const { data } = await api.post("/ndnca/sign", {
        signer_name: form.signer_name,
        signer_fiscal_code: form.signer_fiscal_code,
        signer_address: form.signer_address,
        otp_code: otpCode,
        signature_image_base64: signature,
        language: ndncaLang,
      });
      toast.success(lang === "it" ? "NDNCA firmato" : "NDNCA signed");
      setSigned(true);
      setSignatureMeta(data);
      await refreshUser();
      onSigned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "OTP invalid");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    const resp = await api.get("/ndnca/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ndnca-aaron-properties.pdf";
    a.click();
    URL.revokeObjectURL(url);
  };

  const previewPdf = async () => {
    try {
      const ndncaLang = (typeof navigator !== "undefined" && (navigator.language || "").toLowerCase().startsWith("it")) ? "it" : "en";
      const resp = await api.post("/ndnca/preview", {
        signer_name: form.signer_name,
        signer_fiscal_code: form.signer_fiscal_code,
        signer_address: form.signer_address,
        language: ndncaLang,
      }, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(resp.data));
    } catch {
      toast.error("Preview error");
    }
  };

  const closePreview = () => {
    URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            NDNCA — {lang === "it" ? "Firma con OTP" : "Sign with OTP"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-5 py-2">
          <NDNCAPartiesSection tpl={tpl} form={form} setForm={setForm} userEmail={user?.email} />

          <SignaturePad onChange={setSignature} testId="ndnca-signature-pad" />

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Checkbox id="ndnca-accept" checked={accepted} onCheckedChange={setAccepted} data-testid="ndnca-accept" />
              <Label htmlFor="ndnca-accept" className="text-[#2E2A22] cursor-pointer text-sm">
                {lang === "it" ? "Dichiaro di aver letto e accettato l'NDNCA" : "I accept the NDNCA terms"}
              </Label>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={previewPdf}
              disabled={!form.signer_name}
              data-testid="ndnca-preview-btn"
              className="border-[#C5A880] text-[#4A3F30] bg-transparent hover:bg-[#EBE3D3] rounded-full"
            >
              <Eye className="h-3.5 w-3.5 me-1.5" />
              {lang === "it" ? "Anteprima PDF (BOZZA)" : "Preview PDF (DRAFT)"}
            </Button>
          </div>

          {previewUrl && !signed && (
            <div className="rounded-md border border-[#D8CFBB] bg-[#EBE3D3]/40 p-2" data-testid="ndnca-preview-frame">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[10px] tracking-widest uppercase text-[#C5A880]">
                  {lang === "it" ? "Anteprima documento (BOZZA)" : "Document preview (DRAFT)"}
                </span>
                <button onClick={closePreview} className="text-xs text-[#6B5F4C] hover:text-[#1A1610]">
                  {lang === "it" ? "Chiudi anteprima" : "Close preview"}
                </button>
              </div>
              <iframe
                src={previewUrl}
                title="NDNCA preview"
                className="w-full h-[480px] rounded-md border border-[#D8CFBB] bg-white"
                data-testid="ndnca-preview-iframe"
              />
            </div>
          )}

          {!signed && (
            <NDNCAOtpBox
              otpRequested={otpRequested}
              otpEmail={otpEmail}
              otpCode={otpCode}
              setOtpCode={setOtpCode}
              secondsLeft={secondsLeft}
              busy={busy}
              canRequest={accepted && !!form.signer_name}
              canSign={accepted}
              onRequest={requestOtp}
              onSign={sign}
            />
          )}

          {signed && signatureMeta && <NDNCASignedCard meta={signatureMeta} onDownload={downloadPdf} />}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#4A3F30]">
            {lang === "it" ? "Chiudi" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
